import type { Line } from '../context/context';

export type OsmRouteMode =
  | 'bus' | 'train' | 'tram' | 'subway' | 'light_rail' | 'trolleybus';

export interface OsmRouteMetadata {
  type: 'osmRoute';
  osmRouteId: string;
  mode: OsmRouteMode;
  ref?: string;
  routeName: string;
  from?: string;
  to?: string;
  via?: string;
  operator?: string;
  network?: string;
  // Raw OSM colour tag (already normalized to #rrggbb when recognized).
  // When undefined the renderer falls back to getOsmRouteDefaultColor(mode).
  colour?: string;
  openingHours?: string;
  interval?: string;
  duration?: string;
  wheelchair?: 'yes' | 'no' | 'limited' | 'unknown';
  stopCount: number;
  // Fully-qualified shape IDs of the stop Points this route references,
  // populated by the analysis handler. Lets the map renderer highlight the
  // associated stops when the user clicks the route line.
  stopShapeIds: string[];
}

export type OsmRouteLine = Line & { osmRouteMetadata: OsmRouteMetadata };

export function isOsmRouteLine(line: Line): line is OsmRouteLine {
  return (line as OsmRouteLine).osmRouteMetadata?.type === 'osmRoute';
}

export function getOsmRouteDefaultColor(mode: OsmRouteMode): string {
  switch (mode) {
    case 'bus':         return '#1976d2';
    case 'tram':        return '#d32f2f';
    case 'subway':      return '#388e3c';
    case 'train':
    case 'light_rail':  return '#5d4037';
    case 'trolleybus':  return '#7b1fa2';
  }
}

/**
 * Stable HSL colour derived from a seed string (typically the route ref or
 * name). Used so routes within the same mode get visually distinct colours
 * when OSM doesn't supply a `colour` tag — without this, every bus route
 * defaults to the same blue and the map turns into spaghetti.
 *
 * Fixed saturation/lightness keep all colours in the same legibility band.
 */
export function hashRouteColor(seed: string): string {
  let h = 2166136261; // FNV-1a 32-bit offset basis
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = Math.abs(h) % 360;
  return hslToHex(hue, 62, 46);
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) => lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * Picks the best colour for a route line.
 *
 * Rail-mode routes (tram, subway, train, light_rail) typically have
 * meaningful line colours encoded in OSM — Sydney's L1 grey vs L2 magenta,
 * London Underground lines, etc. We use those.
 *
 * Bus and trolleybus routes overwhelmingly inherit a single operator brand
 * colour, so respecting OSM's `colour` tag turns the map into a wall of the
 * same blue. For these modes we prefer a deterministic hash of the route
 * ref/name so each route is visually distinct, only falling back to the
 * OSM colour or per-mode default if there's nothing to hash.
 */
export function pickRouteColor(opts: {
  osmColour?: string;
  ref?: string;
  name?: string;
  mode: OsmRouteMode;
}): string {
  const seed = opts.ref || opts.name;
  const preferHash = opts.mode === 'bus' || opts.mode === 'trolleybus';
  if (preferHash && seed) return hashRouteColor(`${opts.mode}:${seed}`);
  if (opts.osmColour) return opts.osmColour;
  if (seed) return hashRouteColor(`${opts.mode}:${seed}`);
  return getOsmRouteDefaultColor(opts.mode);
}
